import { neverCancel } from '@mimir/common/async/async';
import { makePriority, PrioritySemaphore } from '@mimir/common/async/prioritySemaphore';
import { invariant } from '@mimir/common/util/invariant';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import type { VideoApiActions } from './apiActions';
import type { UrlArrayResponseDto } from '@mimir/common/dto/urlArrayDtos';
import type { SegmentUrlResolver } from './segmentUrlCache';


export interface ItemSegmentUrlResolver {
  getMediaSegmentUrl(segmentIndex: number): Promise<string>;
}


/**
 * This class implements a url cache for specific stream of a specific item.
 */
export class ItemSegmentUrlCache implements ItemSegmentUrlResolver {
  private semaphore = new PrioritySemaphore(1);
  private urlBlocks: UrlArrayResponseDto[] = [];

  constructor(
    private api: VideoApiActions,
    private itemId: string,
    private method: 'get' | 'put',
    private streamId: StreamRoleId,
    private batchSize: number,

  ) {}

  getMediaSegmentUrl(index: number): Promise<string> {
    /*
      Make sure we don't needlessly request the same information multiple times
      due to concurrency.
    */
    return this.semaphore.limit(() => this.getMediaSegmentUrlCore(index), makePriority(), neverCancel);
  }


  private tryGetUrl(index: number): string | undefined {
    const blockIndex = Math.floor(index / this.batchSize);
    if (this.urlBlocks[blockIndex] === undefined) {
      return undefined;
    }
    const offset = index - this.urlBlocks[blockIndex].first;
    invariant(offset >= 0 && offset < this.urlBlocks[blockIndex].urlSuffixes.length,
      `Index ${index} outside bounds of block ${blockIndex} [${this.urlBlocks[blockIndex].first};${this.urlBlocks[blockIndex].first + this.urlBlocks[blockIndex].urlSuffixes.length - 1}]`);
    return `${this.urlBlocks[blockIndex].prefix}${this.urlBlocks[blockIndex].urlSuffixes[offset]}`;
  }


  private async getMediaSegmentUrlCore(
    index: number
  ): Promise<string> {
    let url = this.tryGetUrl(index);
    if (url !== undefined) {
      return url;
    }

    const blockIndex = Math.floor(index / this.batchSize);
    this.urlBlocks[blockIndex] = await this.api.getUrlArray({
      itemId: this.itemId,
      method: this.method,
      streamId: this.streamId,
      first: blockIndex * this.batchSize,
      last: (blockIndex + 1) * this.batchSize - 1,
    });

    url = this.tryGetUrl(index);
    invariant(
      url != undefined,
      'Missing url for segment index ' + index + ' of stream ' + this.streamId
    );
    return url;
  }
}


/**
 * This class is a wrapper around ItemSegmentUrlCache objects that implements
 * the SegmentUrlResolver interface for drop-in replacement of SegmentUrlCache.
 */
export class SegmentUrlCache2 implements SegmentUrlResolver {

  private caches: Record<string, ItemSegmentUrlCache> = {};

  constructor(
    private api: VideoApiActions,
    private method: 'get' | 'put',
    private batchSize: number,
  ) {}



  getMediaSegmentUrl(itemId: string, streamId: StreamRoleId, segmentIndex: number): Promise<string> {
    const key = `${itemId}:${streamId}`;
    if (!this.caches[key]) {
      this.caches[key] = new ItemSegmentUrlCache(this.api, itemId, this.method, streamId, this.batchSize);
    }

    return this.caches[key].getMediaSegmentUrl(segmentIndex);
  }

}
